import classNames from "classnames";

/** Значения флагов-модификаторов: только `true` добавляет класс; `false`, `null`, `undefined` — нет. */
type ModFlags = Record<string, boolean | null | undefined>;
type Mods = string | string[] | ModFlags | undefined;
type Mix = string | string[] | undefined;

function isModFlags(value: unknown): value is ModFlags {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value)
  );
}

function buildModClasses(base: string, mods?: Mods): string[] {
  if (!mods) {
    return [];
  }

  if (typeof mods === "string") {
    return [`${base}--${mods}`];
  }

  if (Array.isArray(mods)) {
    return mods.filter(Boolean).map((mod) => `${base}--${mod}`);
  }

  return Object.keys(mods)
    .filter((mod) => mods[mod] === true)
    .map((mod) => `${base}--${mod}`);
}

/**
 * BEM-хелпер: `bem("block-name")` → `b`.
 *
 * Корень блока:
 * - `b()` — только блок;
 * - `b(["mod"])` или `b([value])` — модификаторы блока `block--mod`;
 * - `b({ mod: условие })` — флаги: в модификатор попадает только `true`; `false` / `null` / `undefined` эквивалентны «выкл».
 *
 * Элемент:
 * - `b("element")`;
 * - `b("element", ["grow"])` — статичные модификаторы (предпочтительно вместо `{ grow: true }`, если нет условия);
 * - `b("element", { on: flag })` — когда модификаторы от стейта.
 */
export function bem(block: string) {
  return function (
    elementOrMods?: string | string[] | ModFlags,
    modsOrMix?: Mods | Mix,
    maybeMix?: Mix,
  ): string {
    let base = block;
    let mods: Mods;
    let mix: Mix;

    if (typeof elementOrMods === "string") {
      base = `${block}__${elementOrMods}`;
      mods = modsOrMix as Mods;
      mix = maybeMix;
    } else if (elementOrMods === undefined && modsOrMix !== undefined && isModFlags(modsOrMix)) {
      base = block;
      mods = modsOrMix;
      mix = maybeMix;
    } else {
      base = block;
      mods = elementOrMods as Mods;
      mix = modsOrMix as Mix;
    }

    return classNames(base, buildModClasses(base, mods), mix);
  };
}
